import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

type Props = {
  children: ReactNode;
  /** 「展開全部」按鈕文字（可帶被藏起來的數量提示）。 */
  moreLabel?: string;
  /** 「收合」按鈕文字。 */
  lessLabel?: string;
};

/**
 * 等高並排卡片用的「填補留白 → 全展開」容器（flexbox 版）。
 *
 * 放在 flex column 卡片內容的**最後一段**（可漸進顯示的細節）。靠 CSS flexbox 達成，不量測跨卡高度：
 *   - 收合：收合區 `flex: 1 1 var(--fe-peek)` → 預設露出 peek 高度（不是貼地小條），再 flex-grow 吃同列
 *     較高卡 `align-items:stretch` 撐出來的留白，內容超出就 `overflow:hidden` 裁切（底部漸層遮罩）。
 *     peek 只貢獻自己的 basis 給卡片自然高、超出被裁掉不撐爆，所以兩欄不會互相膨脹。
 *   - 內容超過可視區才顯示「展開全部」；展開後 `flex: 0 0 auto`（自然高）→ 卡片變高 → 同列另一欄被撐高，
 *     其收合區自動長高把留白補滿（＝另一欄跟進），全程由 flex 佈局完成、零跨卡量測。
 *   - 整塊 `flex:1 1 auto`（不設 min-height:0）→ 最小高 = 按鈕高，按鈕永遠在卡片框線內、不會被擠出去。
 *
 * JS 只負責偵測「內容是否超過可視區」來開關按鈕 / 漸層，因此不受 ResizeObserver / rAF 在背景分頁被暫停
 * 而造成的高度回授問題（佈局本身是純 CSS）。需配合外層卡片是 flex column（見各卡用法與 index.css 註解）。
 */
export function FillExpand({ children, moreLabel = '展開全部', lessLabel = '收合' }: Props) {
  const regionRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  useLayoutEffect(() => {
    const region = regionRef.current;
    const inner = innerRef.current;
    if (!region || !inner) return;

    let scheduled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const check = () => {
      scheduled = false;
      // 收合時 region 被 flex 限制成「留白高」；內容比它高就是有東西被裁、要給「展開全部」。
      const o = inner.scrollHeight > region.clientHeight + 4;
      setOverflowing((prev) => (prev !== o ? o : prev));
    };
    // setTimeout(0) 去抖：打斷「RO callback 內同步 setState 又被同一個 RO 觀測」的 loop；
    // 不用 rAF（背景／未聚焦分頁會被暫停）。這裡只開關按鈕、不改高度，延後也不影響佈局。
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      timer = setTimeout(check, 0);
    };

    check();
    const ro = new ResizeObserver(schedule);
    ro.observe(region);
    ro.observe(inner);
    window.addEventListener('resize', schedule);
    return () => {
      if (timer) clearTimeout(timer);
      ro.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, []);

  const showBar = expanded || overflowing;
  return (
    <div className={`fill-expand ${expanded ? 'is-expanded' : 'is-collapsed'}`}>
      <div ref={regionRef} className={`fill-expand-region${!expanded && overflowing ? ' is-faded' : ''}`}>
        <div ref={innerRef}>{children}</div>
      </div>
      {showBar && (
        <button
          type="button"
          className="fill-expand-bar"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? `${lessLabel} ▴` : `${moreLabel} ▾`}
        </button>
      )}
    </div>
  );
}
